import { Input, TimetableView, TimetableViewEntryType } from '@bp/ui-components';
import { Dayjs } from 'dayjs';
import { Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-router-dom';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import {
  SubstitutionDataEntry,
  SubstitutionTimetableEntry,
} from '../../../pages/Substitutions/Plan/PlanSubstitutions/utils';
import { use_MergeEventsMutation } from '../../../types/planung-graphql-client-defs';
import { isSame } from '../../../utils/dateCalculations';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { SubstitutionRoomsSelect } from '../components/SubstitutionRoomsSelect';
import styles from './MergeEventForm.module.scss';

type MergeEventFormProps = {
  originTeachers: SubstitutionDataEntry[];
  originRooms: SubstitutionDataEntry[];
  substitutionComment: string;
  eventStart: Dayjs;
  eventEnd: Dayjs;
  eventUuid: string;
  timetableStart: Dayjs;
  timetableEnd: Dayjs;
  timetableEntries: SubstitutionTimetableEntry[];
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

type MergeEventFormType = {
  rooms: string[];
  comment: string;
  selectedEntry: SubstitutionTimetableEntry | null;
};

export const MergeEventForm = ({
  originTeachers,
  originRooms,
  eventStart,
  eventEnd,
  eventUuid,
  substitutionComment,
  timetableStart,
  timetableEnd,
  timetableEntries,
  onLoad,
  onClose,
}: MergeEventFormProps) => {
  const { t } = useTranslation();
  const eventContext = useMemorizedCacheTag('EVENT');

  const [, mergeEvents] = use_MergeEventsMutation();

  const initialValues: MergeEventFormType = {
    rooms: originRooms.map((r) => r.uuid) ?? [],
    comment: substitutionComment,
    selectedEntry: null,
  };

  const originalEntry = timetableEntries.find((entry) => entry.uuid === eventUuid);

  async function handleSubmit(values: MergeEventFormType, formHelpers: FormikHelpers<MergeEventFormType>) {
    onLoad(true);
    if (values.selectedEntry?.uuid) {
      await mergeEvents(
        {
          eventsToMerge: [eventUuid],
          targetEvent: values.selectedEntry?.uuid,
          rooms: values.rooms,
          substitutionComment: values.comment,
        },
        eventContext,
      );
    }
    onLoad(false);
    formHelpers.resetForm();
    onClose();
  }

  return (
    <Formik<MergeEventFormType> initialValues={initialValues} onSubmit={handleSubmit}>
      {({ setFieldValue, values, isSubmitting, dirty, resetForm, errors, submitForm }) => {
        const handleEntrySelect = async (entry: SubstitutionTimetableEntry) => {
          const roomData = entry.rooms;
          if (roomData.length === 0) {
            await setFieldValue('rooms', [...(originRooms.map((r) => r.uuid) ?? [])]);
          } else {
            await setFieldValue('rooms', [...roomData.map((r) => r.uuid)]);
          }

          if (values && values.selectedEntry?.uuid === entry.uuid) {
            await setFieldValue('rooms', [...(originRooms.map((r) => r.uuid) ?? [])]);
            await setFieldValue('selectedEntry', null);
          } else {
            await setFieldValue('selectedEntry', entry);
          }
        };

        return (
          <Form className={styles['merge-event-form']}>
            <div className={styles.info}>
              <div>
                {t('substitutions.initialTeacher')}:
                <span className={styles.bold}>
                  {originTeachers.length > 0 ? originTeachers.map((t) => t.name).join(', ') : t('common.none')}
                </span>
              </div>
              <div>
                {t('substitutions.initialRooms')}:
                <span className={styles.bold}>
                  {originRooms.length > 0 ? originRooms.map((r) => r.name).join(', ') : t('common.none')}
                </span>
              </div>
            </div>

            <div className={styles.hint}>
              <div className={styles['hint-text']}>{t('substitutions.mergeEventHint')}</div>
              <div className={styles.legend}>
                <div className={styles.original}>
                  <div className={styles.example}></div>
                  <div className={styles['legend-text']}>{t('substitutions.mergeCurrentHint')}</div>
                </div>
                <div className={styles.selectable}>
                  <div className={styles.example}></div>
                  <div className={styles['legend-text']}>{t('substitutions.mergeSelectableHint')}</div>
                </div>
                <div className={styles.selected}>
                  <div className={styles.example}></div>
                  <div className={styles['legend-text']}>{t('substitutions.mergeSelectedHint')}</div>
                </div>
              </div>
            </div>

            <div className={styles.timetable}>
              <TimetableView
                gridHeight='s'
                showTimegrid
                start={timetableStart.toDate()}
                end={timetableEnd.toDate()}
                entries={timetableEntries.map((entry) => {
                  const isOriginal = entry.uuid === originalEntry?.uuid;
                  const isSelectable = entry.group && isSame(entry.start, originalEntry?.start, 'hour');
                  const isSelected = entry.uuid === values.selectedEntry?.uuid;

                  // Farben nur nach der Legende: die Fach- bzw. Lehrkraftfarben (bgColors) gingen sonst vor bgColor,
                  // und nicht auswählbare Stunden sähen wie auswählbare aus.
                  let newEntry: TimetableViewEntryType = {
                    ...entry,
                    bgColors: undefined,
                    bgColor: 'var(--color-white)',
                    color: 'var(--color-grey-lighter)',
                    border: '1px solid var(--color-grey-lightest)',
                  };

                  if (isOriginal) {
                    newEntry = {
                      ...entry,
                      bgColors: undefined,
                      bgColor: 'var(--color-grey-lightest)',
                      border: '2px solid var(--color-primary)',
                    };
                  } else if (isSelectable) {
                    newEntry = {
                      ...entry,
                      bgColors: undefined,
                      bgColor: 'var(--color-grey-lightest)',
                      border: '2px solid var(--color-grey-lighter)',
                      onClick: () => handleEntrySelect(entry),
                    };
                  }

                  if (isSelected) {
                    newEntry = {
                      ...entry,
                      bgColors: undefined,
                      bgColor: '#a7dd8e',
                      color: 'var(--color-grey)',
                      border: '2px solid var(--color-success)',
                      onClick: () => handleEntrySelect(entry),
                    };
                  }

                  return newEntry;
                })}
              />
            </div>

            <SubstitutionRoomsSelect
              originalRoomUuids={originRooms.map((r) => r.uuid)}
              disabled={!values.selectedEntry}
              eventRoomUuids={values.rooms}
              eventStart={eventStart}
              eventEnd={eventEnd}
              error={errors.rooms as string}
              onChange={async (uuids) => {
                await setFieldValue('rooms', uuids);
              }}
            />

            <Input
              name='comment'
              label={t('common.comment')}
              onChange={async (e) => setFieldValue('comment', e.target.value)}
              value={values.comment}
            />

            <ModalBottomButtons
              className={styles.bottom}
              closeButton={{
                callback: () => {
                  onClose();
                  resetForm();
                },
              }}
              submitButton={{
                callback: async () => {
                  await submitForm();
                },
                disabled: isSubmitting || !dirty,
              }}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
